// =============================================================================
// Made in Chantier - Application de documentation de chantier
// =============================================================================

// -----------------------------------------------------------------------------
// Imports et Configuration
// -----------------------------------------------------------------------------
import './style.css';
import { translations, videoTypes } from './translations.js';
import { createFFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

// -----------------------------------------------------------------------------
// Constantes
// -----------------------------------------------------------------------------
const VIDEO_WIDTH = 1280;
const VIDEO_HEIGHT = 720;

// -----------------------------------------------------------------------------
// Gestion de FFmpeg - Module principal de traitement vidéo
// -----------------------------------------------------------------------------
const ffmpegManager = (() => {
    let instance = null;
    let loaded = false;
    let loading = false;

    return {
        async load() {
            if (loaded) {
                console.log('FFmpeg déjà chargé');
                return;
            }

            if (loading) {
                console.log('FFmpeg est en cours de chargement...');
                return;
            }

            try {
                loading = true;
                if (!instance) {
                    instance = createFFmpeg({
                        log: true,
                        corePath: 'https://unpkg.com/@ffmpeg/core@0.11.0/dist/ffmpeg-core.js',
                        workerPath: 'https://unpkg.com/@ffmpeg/core@0.11.0/dist/ffmpeg-core.worker.js',
                        wasmPath: 'https://unpkg.com/@ffmpeg/core@0.11.0/dist/ffmpeg-core.wasm'
                    });
                }

                console.log('Démarrage du chargement de FFmpeg...');
                await instance.load();
                console.log('FFmpeg chargé avec succès');
                loaded = true;
            } catch (error) {
                console.error('Erreur lors du chargement de FFmpeg:', error);
                console.error('Détails:', error.message);
                instance = null;
                throw error;
            } finally {
                loading = false;
            }
        },

        isLoaded() {
            return loaded;
        },

        getInstance() {
            if (!loaded) {
                throw new Error('FFmpeg n\'est pas encore chargé. Appelez load() d\'abord.');
            }
            return instance;
        },

        reset() {
            instance = null;
            loaded = false;
            loading = false;
        }
    };
})();

// Charger FFmpeg au démarrage
async function initializeFFmpeg() {
    try {
        await ffmpegManager.load();
        console.log('FFmpeg chargé avec succès au démarrage');
        // Activer le bouton de génération
        const generateBtn = document.getElementById('generate-video');
        if (generateBtn) {
            generateBtn.disabled = false;
        }
    } catch (error) {
        console.error('Erreur fatale lors du chargement de FFmpeg:', error);
        alert('Erreur lors du chargement de FFmpeg. Veuillez rafraîchir la page.');
        const generateBtn = document.getElementById('generate-video');
        if (generateBtn) {
            generateBtn.disabled = true;
        }
    }
}

// Initialiser FFmpeg
initializeFFmpeg();

// -----------------------------------------------------------------------------
// Variables Globales - Gestion des Médias
// -----------------------------------------------------------------------------
let videoStream = null;        // Flux vidéo actif de la caméra
let audioBlob = null;         // Enregistrement audio temporaire
let capturedPhotos = [];      // Photos capturées avec leurs métadonnées
let currentCameraId = null;   // ID de la caméra active
let mediaRecorder = null;     // Enregistreur audio actif
let recordingStartTime = null; // Horodatage du début d'enregistrement

// -----------------------------------------------------------------------------
// Interface de Progression - Feedback Utilisateur
// -----------------------------------------------------------------------------
const progressInterface = document.createElement('div');
progressInterface.className = 'progress-interface';
progressInterface.style.display = 'none';
document.body.appendChild(progressInterface);

const progressStatus = document.createElement('div');
progressStatus.className = 'progress-status';
progressInterface.appendChild(progressStatus);

// -----------------------------------------------------------------------------
// Éléments DOM - Interface Utilisateur
// -----------------------------------------------------------------------------

// Formulaire Principal
const titleInput = document.getElementById('title');
const videoTypeSelect = document.getElementById('video-type');
const locationInput = document.getElementById('location');
const useLocationButton = document.getElementById('use-location');

// Contrôles Caméra et Photo
const openCameraButton = document.getElementById('open-camera');
const closeCameraButton = document.getElementById('close-camera');
const takePhotoButton = document.getElementById('take-photo');
const videoPreview = document.getElementById('video-preview');
const photoGallery = document.getElementById('photo-gallery');

// Gestion des Fichiers et Actions
const fileInput = document.getElementById('file-input');
const recordButton = document.getElementById('record-button');
const generateButton = document.getElementById('generate-button');

// Menu et Navigation
const menuToggle = document.getElementById('menu-toggle');
const menuOverlay = document.getElementById('menu-overlay');
const menuClose = document.getElementById('menu-close');
const currentLanguageBtn = document.getElementById('current-language');
const languageDropdown = document.querySelector('.language-dropdown');
const languageOptions = document.querySelectorAll('.language-option');

// Fonction pour traduire l'interface
function translateUI(lang) {
    document.documentElement.lang = lang;
    document.querySelectorAll('[data-i18n]').forEach(element => {
        const key = element.getAttribute('data-i18n');
        if (translations[lang] && translations[lang][key]) {
            if (element.tagName === 'INPUT') {
                element.placeholder = translations[lang][key];
            } else if (element.tagName === 'OPTION' && element.value === '') {
                // Pour l'option par défaut du select de type de vidéo
                element.textContent = translations[lang][key];
            } else {
                element.textContent = translations[lang][key];
            }
        }
    });

    document.querySelectorAll('[data-i18n-title]').forEach(element => {
        const key = element.getAttribute('data-i18n-title');
        if (translations[lang] && translations[lang][key]) {
            element.title = translations[lang][key];
        }
    });

    // Mise à jour du titre de la page
    document.title = translations[lang].title;

    // Mettre à jour le bouton de langue actif
    languageOptions.forEach(option => {
        option.classList.toggle('active', option.dataset.lang === lang);
    });
}

// Gestion du menu
function toggleMenu() {
    menuOverlay.classList.toggle('hidden');
    document.body.style.overflow = menuOverlay.classList.contains('hidden') ? '' : 'hidden';
}

// Événements du menu
menuToggle.addEventListener('click', toggleMenu);
menuClose.addEventListener('click', toggleMenu);
menuOverlay.addEventListener('click', (e) => {
    if (e.target === menuOverlay) {
        toggleMenu();
    }
});

// Gestion du menu déroulant des langues
currentLanguageBtn.addEventListener('click', (e) => {
    const isExpanded = currentLanguageBtn.getAttribute('aria-expanded') === 'true';
    currentLanguageBtn.setAttribute('aria-expanded', !isExpanded);
    languageDropdown.classList.toggle('hidden');
});

// Initialisation des événements de langue
languageOptions.forEach(option => {
    option.addEventListener('click', () => {
        const lang = option.dataset.lang;
        translateUI(lang);
        languageDropdown.classList.add('hidden');
        currentLanguageBtn.setAttribute('aria-expanded', 'false');
    });
});

// Initialisation de la langue par défaut
translateUI('fr');

// Initialisation des types de vidéo
videoTypes.forEach(type => {
    const option = document.createElement('option');
    option.value = type.id;
    option.textContent = type.label;
    videoTypeSelect.appendChild(option);
});

// Fermer le menu si on clique ailleurs
document.addEventListener('click', (e) => {
    if (!e.target.closest('.language-selector')) {
        currentLanguageBtn.setAttribute('aria-expanded', 'false');
        languageDropdown.classList.add('hidden');
    }
});

// Gestion des langues - Un seul gestionnaire d'événements
function initializeLanguageHandlers() {
    if (!languageOptions || !currentLanguageBtn || !languageDropdown) return;

    languageOptions.forEach(option => {
        option.addEventListener('click', () => {
            const selectedLang = option.dataset.lang;
            const flag = option.querySelector('.flag')?.textContent;
            const shortName = selectedLang.toUpperCase();

            // Mettre à jour le bouton principal
            const currentFlag = currentLanguageBtn.querySelector('.flag');
            const currentName = currentLanguageBtn.querySelector('.lang-name');
            if (currentFlag && currentName && flag) {
                currentFlag.textContent = flag;
                currentName.textContent = shortName;
            }

            // Changer la langue
            localStorage.setItem('preferredLanguage', selectedLang);
            translateUI(selectedLang);

            // Fermer le menu
            currentLanguageBtn.setAttribute('aria-expanded', 'false');
            languageDropdown.classList.add('hidden');
        });
    });
}

// Initialiser les gestionnaires de langue
initializeLanguageHandlers();

// Initialiser la langue par défaut
const defaultLang = localStorage.getItem('preferredLanguage') || 'fr';
const defaultLangBtn = document.querySelector(`.language-option[data-lang="${defaultLang}"]`);
if (defaultLangBtn) {
    const flag = defaultLangBtn.querySelector('.flag').textContent;
    currentLanguageBtn.querySelector('.flag').textContent = flag;
    currentLanguageBtn.querySelector('.lang-name').textContent = defaultLang.toUpperCase();
}

// Appliquer la langue par défaut
translateUI(defaultLang);

// Get DOM elements additionnels
const cameraModal = document.getElementById('camera-modal');
const captureButton = document.getElementById('capture-photo');
const geolocateButton = document.getElementById('geolocate');
const photosContainer = document.getElementById('photos-container');
const cameraSelect = document.getElementById('camera-select');

// Fonction pour formater la date en français
function formatDateFr(date) {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
}

// Formater le temps d'enregistrement
function formatRecordingTime(ms) {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Désactiver l'édition directe du titre
titleInput.addEventListener('input', (e) => {
    e.preventDefault();
    return false;
});

// Fonction pour générer le titre
function generateTitle() {
    const videoType = videoTypeSelect.value;
    const location = locationInput.value;
    const date = formatDateFr(new Date());

    const typeLabel = videoTypes.find(t => t.id === videoType)?.label || '';
    if (typeLabel) {
        titleInput.value = location ?
            `${typeLabel} - ${location} - ${date}` :
            `${typeLabel} - ${date}`;
    } else {
        titleInput.value = '';
    }
}

// Écouteurs d'événements pour la génération du titre
videoTypeSelect.addEventListener('change', generateTitle);
locationInput.addEventListener('change', generateTitle);
locationInput.addEventListener('input', generateTitle);

// Générer le titre initial si des valeurs sont déjà présentes
if (videoTypeSelect.value || locationInput.value) {
    generateTitle();
}



// Initialisation et gestion du chargement de fichiers
function initializeFileUpload() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);

    const uploadButton = document.getElementById('upload-photos');
    if (!uploadButton) return;

    // Gérer le clic sur le bouton d'upload
    uploadButton.addEventListener('click', () => fileInput.click());

    // Gérer la sélection de fichiers
    fileInput.addEventListener('change', async (event) => {
        const files = event.target.files;
        if (!files?.length) return;

        for (const file of files) {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result) {
                        addPhotoToGallery(e.target.result);
                    }
                };
                reader.readAsDataURL(file);
            }
        }
        fileInput.value = '';
    });
}

// Initialiser le système de chargement de fichiers
initializeFileUpload();

// Initialisation et gestion de la géolocalisation
function initializeGeolocation() {
    const geoButton = document.getElementById('geolocate');
    const locationInput = document.getElementById('location');
    if (!geoButton || !locationInput) return;

    geoButton.addEventListener('click', async () => {
        try {
            // Ajouter l'état de chargement
            geoButton.disabled = true;
            geoButton.classList.add('location-loading');
            locationInput.placeholder = 'Recherche de votre position...';

            // Récupérer la position actuelle
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                });
            });

            const { latitude, longitude } = position.coords;

            // Utiliser OpenStreetMap Nominatim pour la géocodification inverse
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
                { headers: { 'Accept-Language': 'fr' } }
            );

            if (!response.ok) throw new Error('Erreur lors de la recherche d\'adresse');

            const data = await response.json();

            // Formater l'adresse
            const address = data.address;
            if (!address) throw new Error('Adresse non trouvée');

            const formattedAddress = [
                address.house_number,
                address.road,
                address.postcode,
                address.city
            ].filter(Boolean).join(' ');

            locationInput.value = formattedAddress;
            generateTitle(); // Mettre à jour le titre avec la nouvelle localisation

        } catch (error) {
            console.error('Geolocation error:', error);
            let errorMessage = 'Erreur lors de la géolocalisation';

            if (error.code === 1) {
                errorMessage = 'Veuillez autoriser l\'accès à votre position';
            } else if (error.code === 2) {
                errorMessage = 'Position non disponible';
            } else if (error.code === 3) {
                errorMessage = 'Délai d\'attente dépassé';
            }

            alert(errorMessage);
        } finally {
            // Retirer l'état de chargement
            geoButton.disabled = false;
            geoButton.classList.remove('location-loading');
            locationInput.placeholder = 'Localisation';
        }
    });
}

// Initialiser la géolocalisation
initializeGeolocation();



// Camera functions
async function initializeCamera() {
    try {
        // Get list of available cameras
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');

        // Update camera select options
        cameraSelect.innerHTML = videoDevices.map(device =>
            `<option value="${device.deviceId}">${device.label || `Caméra ${videoDevices.indexOf(device) + 1}`}</option>`
        ).join('');

        // Use the first camera by default or the previously selected one
        const cameraId = currentCameraId || videoDevices[0].deviceId;
        await switchCamera(cameraId);

        cameraModal.classList.remove('hidden');

        // Add camera switch event listener
        cameraSelect.addEventListener('change', (e) => switchCamera(e.target.value));
    } catch (error) {
        console.error('Error accessing camera:', error);
        alert('Erreur lors de l\'accès à la caméra');
    }
}

// Function to switch between cameras
async function switchCamera(cameraId) {
    try {
        // Stop current stream if it exists
        if (videoStream) {
            videoStream.getTracks().forEach(track => track.stop());
        }

        // Start new stream with selected camera
        videoStream = await navigator.mediaDevices.getUserMedia({
            video: {
                deviceId: { exact: cameraId },
                width: { ideal: VIDEO_WIDTH },
                height: { ideal: VIDEO_HEIGHT }
            }
        });

        const videoElement = document.getElementById('camera-preview');
        videoElement.srcObject = videoStream;
        await videoElement.play();

        // Save current camera ID
        currentCameraId = cameraId;
    } catch (error) {
        console.error('Error switching camera:', error);
        alert('Erreur lors du changement de caméra');
    }
}

function stopCamera() {
    if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
        videoStream = null;
    }
    cameraModal.classList.add('hidden');
}

// -----------------------------------------------------------------------------
// Gestionnaires d'Événements - Caméra
// -----------------------------------------------------------------------------
openCameraButton.addEventListener('click', initializeCamera);
closeCameraButton.addEventListener('click', stopCamera);

captureButton.addEventListener('click', () => {
    const videoElement = document.getElementById('camera-preview');
    const canvas = document.createElement('canvas');
    canvas.width = VIDEO_WIDTH;
    canvas.height = VIDEO_HEIGHT;
    const context = canvas.getContext('2d');
    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    addPhotoToGallery(photoData);
    // Garder la caméra ouverte pour prendre d'autres photos
});

// -----------------------------------------------------------------------------
// Gestion des Photos et de l'Audio
// -----------------------------------------------------------------------------

/**
 * Ajoute une photo à la galerie avec ses métadonnées
 * @param {string} photoData - Données de l'image en base64
 */
function addPhotoToGallery(photoData) {
    const photosContainer = document.getElementById('photos-container');
    const template = document.getElementById('photo-template');
    const photoItem = template.content.cloneNode(true);
    const container = photoItem.querySelector('.photo-item');
    const img = photoItem.querySelector('.photo-preview');
    img.src = photoData;
    img.alt = 'Photo capturée';

    // Initialiser les données audio
    container.dataset.audioBlob = '';
    container.dataset.audioDuration = '0';

    // Gérer le bouton d'enregistrement
    const recordButton = photoItem.querySelector('.record-audio');
    const recordingTime = photoItem.querySelector('.recording-time');
    let mediaRecorder = null;
    let audioChunks = [];
    let startTime = 0;
    let recordingInterval = null;

    recordButton.addEventListener('click', async () => {
        if (!mediaRecorder) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaRecorder = new MediaRecorder(stream);

                mediaRecorder.ondataavailable = (event) => {
                    audioChunks.push(event.data);
                };

                mediaRecorder.onstop = () => {
                    const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
                    const audioUrl = URL.createObjectURL(audioBlob);
                    container.dataset.audioBlob = audioUrl;
                    container.dataset.audioDuration = String(Date.now() - startTime);
                    container.classList.add('has-audio');
                    recordButton.classList.remove('recording');
                    clearInterval(recordingInterval);
                    recordingTime.textContent = formatRecordingTime(Date.now() - startTime);
                    stream.getTracks().forEach(track => track.stop());

                    // Mettre à jour les données de la photo
                    const index = capturedPhotos.findIndex(p => p.image === photoData);
                    if (index > -1) {
                        capturedPhotos[index].audio = audioBlob;
                        capturedPhotos[index].audioDuration = Date.now() - startTime;
                    }
                };

                // Démarrer l'enregistrement
                audioChunks = [];
                mediaRecorder.start();
                startTime = Date.now();
                recordButton.classList.add('recording');

                // Mettre à jour le temps d'enregistrement
                recordingInterval = setInterval(() => {
                    const elapsed = Date.now() - startTime;
                    recordingTime.textContent = formatRecordingTime(elapsed);
                }, 100);

            } catch (err) {
                console.error('Erreur lors de l\'accès au microphone:', err);
                alert('Impossible d\'accéder au microphone');
            }
        } else if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            mediaRecorder = null;
        }
    });

    // Gérer le bouton de suppression
    const deleteButton = photoItem.querySelector('.delete-photo');
    deleteButton.addEventListener('click', () => {
        // Nettoyer l'URL de l'audio si elle existe
        if (container.dataset.audioBlob) {
            URL.revokeObjectURL(container.dataset.audioBlob);
        }
        // Supprimer la photo du tableau
        const index = capturedPhotos.findIndex(p => p.image === photoData);
        if (index > -1) {
            capturedPhotos.splice(index, 1);
        }
        // Supprimer l'élément du DOM
        container.remove();
    });

    // Ajouter la photo au tableau des photos capturées
    const photoInfo = {
        image: photoData,
        audio: null,
        audioDuration: 0
    };
    capturedPhotos.push(photoInfo);

    photosContainer.appendChild(photoItem);
}

// Audio recording functions
async function startAudioRecording() {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        const audioChunks = [];

        // Create recording interface
        const recordingInterface = document.createElement('div');
        recordingInterface.className = 'recording-interface';
        const recordingStatus = document.createElement('div');
        recordingStatus.textContent = 'Enregistrement en cours...';
        const stopButton = document.createElement('button');
        stopButton.textContent = 'Arrêter l\'enregistrement';
        stopButton.className = 'primary-button';

        recordingInterface.appendChild(recordingStatus);
        recordingInterface.appendChild(stopButton);
        document.body.appendChild(recordingInterface);

        mediaRecorder.ondataavailable = (event) => {
            audioChunks.push(event.data);
        };

        mediaRecorder.onstop = () => {
            audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
            const audioUrl = URL.createObjectURL(audioBlob);

            // Create audio preview
            const audio = document.createElement('audio');
            audio.src = audioUrl;
            audio.controls = true;
            recordingStatus.textContent = 'Enregistrement terminé';
            recordingInterface.appendChild(audio);

            // Add close button
            const closeButton = document.createElement('button');
            closeButton.textContent = 'Fermer';
            closeButton.className = 'primary-button';
            closeButton.onclick = () => recordingInterface.remove();
            recordingInterface.appendChild(closeButton);
        };

        mediaRecorder.start();
        stopButton.onclick = () => {
            mediaRecorder.stop();
            stream.getTracks().forEach(track => track.stop());
            stopButton.disabled = true;
        };

    } catch (error) {
        console.error('Error recording audio:', error);
        alert('Erreur lors de l\'enregistrement audio');
    }
};


// -----------------------------------------------------------------------------
// Génération d'Images
// -----------------------------------------------------------------------------

/**
 * Crée une image de titre pour la vidéo
 * @param {string} title - Titre de la vidéo
 * @param {string} location - Localisation du chantier
 * @returns {Promise<Uint8Array>} Données de l'image en format binaire
 */
async function createTitleImage(title, location) {
    const canvas = document.createElement('canvas');
    canvas.width = VIDEO_WIDTH;
    canvas.height = VIDEO_HEIGHT;
    const ctx = canvas.getContext('2d');

    // Fill background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw title
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(title || 'Made in Chantier', canvas.width / 2, canvas.height / 2 - 30);

    // Draw location
    if (location) {
        ctx.font = '32px Arial';
        ctx.fillText(location, canvas.width / 2, canvas.height / 2 + 30);
    }

    // Convert to Uint8Array
    const dataUrl = canvas.toDataURL('image/png');
    const data = dataUrl.split(',')[1];
    return Uint8Array.from(atob(data), c => c.charCodeAt(0));
}

// -----------------------------------------------------------------------------
// Génération de Vidéo et Gestion FFmpeg
// -----------------------------------------------------------------------------

// Fonction pour obtenir la durée d'un fichier audio
async function getAudioDuration(ffmpeg, audioFile) {
    try {
        // Utiliser ffprobe pour obtenir la durée
        await ffmpeg.run(
            '-i', audioFile,
            '-show_entries', 'format=duration',
            '-v', 'quiet',
            '-of', 'csv=p=0',
            'duration.txt'
        );

        // Lire le fichier de durée
        const durationBuffer = await ffmpeg.FS('readFile', 'duration.txt');
        const duration = parseFloat(durationBuffer.toString());

        // Nettoyer le fichier temporaire
        await ffmpeg.FS('unlink', 'duration.txt');

        // Retourner la durée (minimum 3 secondes)
        return Math.max(duration, 3);
    } catch (error) {
        console.warn(`Impossible de lire la durée de ${audioFile}:`, error);
        return 3; // Durée par défaut
    }
}

/**
 * Nettoie les fichiers temporaires créés par FFmpeg
 * @param {FFmpeg} ffmpeg - Instance FFmpeg
 * @param {boolean} hasAudio - Si la vidéo contient de l'audio
 * @param {number} photoCount - Nombre de photos dans la vidéo
 */
const cleanupFFmpegFiles = (ffmpeg, hasAudio, photoCount) => {
    try {
        ffmpeg.FS('unlink', 'title.png');
        for (let i = 0; i < photoCount; i++) {
            ffmpeg.FS('unlink', `photo${i}.png`);
            if (hasAudio) {
                try {
                    ffmpeg.FS('unlink', `audio${i}.wav`);
                } catch (e) {
                    console.log(`Pas d'audio à nettoyer pour la photo ${i}`);
                }
            }
        }
        if (hasAudio) {
            try {
                ffmpeg.FS('unlink', 'audio_list.txt');
                ffmpeg.FS('unlink', 'combined_audio.wav');
                ffmpeg.FS('unlink', 'temp_with_video.mp4');
            } catch (e) {
                console.log('Certains fichiers audio temporaires n\'existaient pas');
            }
        }
        try {
            ffmpeg.FS('unlink', 'output.mp4');
        } catch (e) {
            console.log('Pas de fichier de sortie à nettoyer');
        }
    } catch (e) {
        console.error('Erreur lors du nettoyage des fichiers:', e);
    }
};

// -----------------------------------------------------------------------------
// Gestionnaires d'Événements - Génération Vidéo
// -----------------------------------------------------------------------------
document.getElementById('generate-video').addEventListener('click', async () => {
    if (capturedPhotos.length === 0) {
        alert('Veuillez prendre au moins une photo');
        return;
    }

    const generateBtn = document.getElementById('generate-video');
    generateBtn.disabled = true;
    generateBtn.textContent = 'Génération en cours...';

    const progressInterface = document.createElement('div');
    progressInterface.className = 'progress-interface';
    progressInterface.innerHTML = `
        <div class="progress-status">Initialisation de FFmpeg...</div>`;
    document.body.appendChild(progressInterface);
    const progressStatus = progressInterface.querySelector('.progress-status');

    let ffmpeg = null;

    try {
        // S'assurer que FFmpeg est chargé
        progressStatus.textContent = 'Chargement de FFmpeg...';
        if (!ffmpegManager.isLoaded()) {
            await ffmpegManager.load();
        }
        ffmpeg = ffmpegManager.getInstance();

        // Créer l'image du titre
        progressStatus.textContent = 'Création du titre...';
        const title = document.getElementById('title').value;
        const location = document.getElementById('location').value;
        const titleImageData = await createTitleImage(title, location);
        const titleArray = new Uint8Array(titleImageData);
        ffmpeg.FS('writeFile', 'title.png', titleArray);

        // Traiter les photos et l'audio
        progressStatus.textContent = 'Préparation des médias...';
        let hasAudio = false;
        for (let i = 0; i < capturedPhotos.length; i++) {
            const photo = capturedPhotos[i];
            const photoData = photo.image.split(',')[1];
            ffmpeg.FS('writeFile', `photo${i}.png`, base64ToUint8Array(photoData));

            if (photo.audioBlob) {
                hasAudio = true;
                const audioArrayBuffer = await photo.audioBlob.arrayBuffer();
                const audioData = new Uint8Array(audioArrayBuffer);
                ffmpeg.FS('writeFile', `audio${i}.wav`, audioData);
            }
        }

        // Générer la vidéo
        progressStatus.textContent = 'Génération de la vidéo...';

        // Préparer les durées d'affichage
        let photoDurations = [];
        if (hasAudio) {
            for (let i = 0; i < capturedPhotos.length; i++) {
                if (capturedPhotos[i].audioBlob) {
                    const duration = await getAudioDuration(ffmpeg, `audio${i}.wav`);
                    photoDurations[i] = duration;
                } else {
                    photoDurations[i] = 3;
                }
            }
        } else {
            photoDurations = Array(capturedPhotos.length).fill(3);
        }

        // Générer la vidéo avec les durées appropriées
        let filterComplex = [];
        let inputs = [];

        // Ajouter le titre avec fade out
        inputs.push('[0:v]fade=t=out:st=2.5:d=0.5[v0];');

        // Ajouter chaque photo avec fade in/out
        for (let i = 0; i < capturedPhotos.length; i++) {
            const inputIndex = i + 1;
            const duration = photoDurations[i];
            const fadeIn = i === 0 ? '' : 'fade=t=in:st=0:d=0.5:';
            const fadeOut = i === capturedPhotos.length - 1 ? '' : `fade=t=out:st=${duration - 0.5}:d=0.5:`;
            inputs.push(`[${inputIndex}:v]${fadeIn}${fadeOut}[v${inputIndex}];`);
        }

        // Concaténer toutes les vidéos
        const concatInputs = Array.from({ length: capturedPhotos.length + 1 }, (_, i) => `[v${i}]`).join('');
        filterComplex.push(`${inputs.join('')}${concatInputs}concat=n=${capturedPhotos.length + 1}:v=1:a=0[outv]`);

        // Construire la commande d'entrée
        let inputCommand = `-loop 1 -framerate 30 -t 3 -i title.png `;
        for (let i = 0; i < capturedPhotos.length; i++) {
            inputCommand += `-loop 1 -framerate 30 -t ${photoDurations[i]} -i photo${i}.png `;
        }

        await ffmpeg.run(
            ...inputCommand.split(' '),
            '-filter_complex',
            filterComplex.join(''),
            '-map', '[outv]',
            '-c:v', 'libx264',
            '-pix_fmt', 'yuv420p',
            '-movflags', '+faststart',
            'output.mp4'
        );

        // Ajouter l'audio si présent
        if (hasAudio) {
            progressStatus.textContent = 'Ajout de l\'audio...';
            ffmpeg.FS('rename', 'output.mp4', 'temp_with_video.mp4');

            // Concaténer les fichiers audio
            let audioFiles = [];
            for (let i = 0; i < capturedPhotos.length; i++) {
                if (capturedPhotos[i].audioBlob) {
                    audioFiles.push(`audio${i}.wav`);
                }
            }

            if (audioFiles.length > 0) {
                // Créer la liste des fichiers audio avec leurs durées réelles
                const audioList = audioFiles.map((file, i) => 
                    `file '${file}'
duration ${photoDurations[i]}`
                ).join('\n');
                ffmpeg.FS('writeFile', 'audio_list.txt', audioList);

                await ffmpeg.run(
                    '-f', 'concat',
                    '-safe', '0',
                    '-i', 'audio_list.txt',
                    '-c', 'copy',
                    'combined_audio.wav'
                );

                await ffmpeg.run(
                    '-i', 'temp_with_video.mp4',
                    '-i', 'combined_audio.wav',
                    '-c:v', 'copy',
                    '-c:a', 'aac',
                    'output.mp4'
                );
            }
        }
        // Lire le fichier de sortie
        progressStatus.textContent = 'Finalisation de la vidéo...';
        const data = ffmpeg.FS('readFile', 'output.mp4');

        // Préparer la vidéo pour la modale
        const blob = new Blob([data.buffer], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `video_${timestamp}.mp4`;

        // Afficher la vidéo dans la modale
        const videoModal = document.getElementById('video-modal');
        const videoPreview = document.getElementById('video-preview');
        const downloadButton = document.getElementById('download-video');
        const closeButton = videoModal.querySelector('.modal-close');

        // Configurer la vidéo
        videoPreview.src = url;
        videoPreview.load();

        // Configurer le bouton de téléchargement
        downloadButton.onclick = () => {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        };

        // Gestionnaire de fermeture de la modale
        const closeModal = () => {
            videoModal.classList.add('hidden');
            videoPreview.pause();
            videoPreview.src = '';
            URL.revokeObjectURL(url);
        };

        closeButton.onclick = closeModal;
        videoModal.onclick = (e) => {
            if (e.target === videoModal) closeModal();
        };

        // Afficher la modale
        videoModal.classList.remove('hidden');
        progressInterface.remove();
        progressStatus.textContent = 'Vidéo générée avec succès!';

        // Nettoyer les fichiers temporaires
        await cleanupFFmpegFiles(ffmpeg, hasAudio, capturedPhotos.length);
    } catch (error) {
        console.error('Erreur lors de la génération de la vidéo:', error);
        progressStatus.textContent = `Erreur: ${error.message}`;
    } finally {
        progressInterface.remove();
        generateBtn.disabled = false;
        generateBtn.textContent = 'Générer la vidéo';
    }

});

// Fonction pour convertir un base64 en Uint8Array
function base64ToUint8Array(base64) {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
};
